import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { downloadReport } from "@/lib/api";
import { inr, fmtDate, billNo } from "@/lib/format";
import { PageTitle } from "@/components/PageTitle";
import { DatePicker } from "@/components/DatePicker";
import { SearchSelect } from "@/components/SearchSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FileText, FileDown, FileSpreadsheet, Search } from "lucide-react";
import { toast } from "sonner";

export default function PartyStatement() {
  const [mode, setMode] = useState("party"); // party | category
  const [supplierId, setSupplierId] = useState("");
  const [category, setCategory] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState(null);
  const [includeProducts, setIncludeProducts] = useState(false);

  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers-min"], queryFn: async () => (await api.get("/suppliers")).data });
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/suppliers/categories")).data });

  const params = () => ({
    supplier_id: mode === "party" ? supplierId : undefined,
    category: mode === "category" ? category : undefined,
    date_from: from || undefined,
    date_to: to || undefined,
  });

  const { data, isFetching } = useQuery({
    queryKey: ["party-statement", applied],
    queryFn: async () => (await api.get("/party/statement", { params: applied })).data,
    enabled: !!applied,
  });

  const canRun = (mode === "party" && supplierId) || (mode === "category" && category);

  const view = () => {
    if (!canRun) { toast.error("Select a party or category first"); return; }
    setApplied(params());
  };

  const exportIt = async (fmt) => {
    if (!canRun) { toast.error("Select a party or category first"); return; }
    try { await downloadReport("/party/statement/export", { ...params(), fmt, include_products: fmt === "pdf" ? includeProducts : undefined }, "party_statement"); toast.success(`Statement (${fmt.toUpperCase()}) downloaded`); }
    catch { toast.error("Export failed"); }
  };

  return (
    <div>
      <PageTitle title="Party Statement" subtitle="Opening balance, purchases, payments, pending & closing balance for a party or category" />

      <Card className="p-4 mb-4">
        <div className="grid lg:grid-cols-5 gap-3 lg:items-end">
          <div>
            <Label className="text-xs">Statement For</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1" data-testid="ps-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="party">Single Party</SelectItem><SelectItem value="category">Category</SelectItem></SelectContent>
            </Select>
          </div>
          {mode === "party" ? (
            <div className="lg:col-span-2">
              <Label className="text-xs">Party (Supplier)</Label>
              <div className="mt-1">
                <SearchSelect value={supplierId} onChange={setSupplierId}
                  options={suppliers.map((s) => ({ value: s.id, label: `${s.supplier_code} — ${s.name}` }))}
                  placeholder="Select supplier" searchPlaceholder="Type supplier name or code…" testid="ps-supplier" />
              </div>
            </div>
          ) : (
            <div className="lg:col-span-2">
              <Label className="text-xs">Category</Label>
              <div className="mt-1">
                <SearchSelect value={category} onChange={setCategory}
                  options={cats.map((c) => ({ value: c, label: c }))}
                  placeholder="Select category" searchPlaceholder="Type category…" testid="ps-category" />
              </div>
            </div>
          )}
          <div><Label className="text-xs">From</Label><div className="mt-1"><DatePicker value={from} onChange={setFrom} testid="ps-from" /></div></div>
          <div><Label className="text-xs">To</Label><div className="mt-1"><DatePicker value={to} onChange={setTo} testid="ps-to" /></div></div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button onClick={view} data-testid="ps-view"><Search className="h-4 w-4" /> View Statement</Button>
          <Button variant="outline" onClick={() => exportIt("pdf")} data-testid="ps-pdf"><FileDown className="h-4 w-4" /> PDF</Button>
          <Button variant="outline" onClick={() => exportIt("excel")} data-testid="ps-excel"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
          <label className="flex items-center gap-2 text-sm cursor-pointer ml-2 select-none">
            <Checkbox checked={includeProducts} onCheckedChange={(v) => setIncludeProducts(!!v)} data-testid="ps-include-products" />
            Include product details in PDF
          </label>
        </div>
      </Card>

      {isFetching && <Card className="p-10 text-center text-muted-foreground">Loading…</Card>}

      {data && !isFetching && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <Card className="p-4"><div className="text-xs text-muted-foreground">Opening Balance</div><div className="font-display text-xl font-bold data-num">{inr(data.opening_balance)}</div></Card>
            <Card className="p-4"><div className="text-xs text-muted-foreground">Purchases</div><div className="font-display text-xl font-bold data-num">{inr(data.period_purchase)}</div></Card>
            <Card className="p-4"><div className="text-xs text-muted-foreground">Payments</div><div className="font-display text-xl font-bold data-num text-success">{inr(data.period_payment)}</div></Card>
            <Card className="p-4"><div className="text-xs text-muted-foreground">Closing Balance</div><div className="font-display text-xl font-bold data-num text-destructive">{inr(data.closing_balance)}</div></Card>
            <Card className="p-4"><div className="text-xs text-muted-foreground">Pending</div><div className="font-display text-xl font-bold data-num text-destructive">{inr(data.pending_total)}</div></Card>
          </div>

          <Card className="overflow-hidden mb-4">
            <div className="p-4 border-b flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /><h3 className="font-display font-bold">{data.party_name} — Ledger</h3></div>
            <div className="overflow-x-auto ssacc-scroll">
              <Table>
                <TableHeader><TableRow className="bg-muted/50">
                  <TableHead>Date</TableHead><TableHead>Particulars</TableHead>
                  <TableHead className="text-right">Purchase</TableHead><TableHead className="text-right">Payment</TableHead><TableHead className="text-right">Balance</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  <TableRow className="bg-accent/10 font-medium">
                    <TableCell>{applied?.date_from ? fmtDate(applied.date_from) : "—"}</TableCell>
                    <TableCell>Opening Balance</TableCell>
                    <TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right data-num">{inr(data.opening_balance)}</TableCell>
                  </TableRow>
                  {data.lines.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No transactions in this period.</TableCell></TableRow>
                  ) : data.lines.map((ln, i) => (
                    <TableRow key={i} data-testid="ps-line" className={ln.kind === "payment" ? "bg-muted/20" : ""}>
                      <TableCell className="whitespace-nowrap">{fmtDate(ln.date)}</TableCell>
                      <TableCell className={ln.kind === "payment" ? "pl-8 text-success" : "font-medium"}>{ln.particulars}</TableCell>
                      <TableCell className="text-right data-num">{ln.debit ? inr(ln.debit) : "—"}</TableCell>
                      <TableCell className="text-right data-num text-success">{ln.credit ? inr(ln.credit) : "—"}</TableCell>
                      <TableCell className="text-right data-num font-medium">{inr(ln.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-primary/5 font-bold">
                    <TableCell></TableCell><TableCell>Closing Balance</TableCell>
                    <TableCell className="text-right data-num">{inr(data.period_purchase)}</TableCell>
                    <TableCell className="text-right data-num">{inr(data.period_payment)}</TableCell>
                    <TableCell className="text-right data-num">{inr(data.closing_balance)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="p-4 border-b"><h3 className="font-display font-bold">Pending Bills ({data.pending_bills.length})</h3></div>
            <div className="overflow-x-auto ssacc-scroll">
              <Table>
                <TableHeader><TableRow className="bg-muted/50"><TableHead>Bill No</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.pending_bills.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No pending bills.</TableCell></TableRow>
                  ) : data.pending_bills.map((b, i) => (
                    <TableRow key={i} data-testid="ps-pending">
                      <TableCell className="font-semibold text-primary">{billNo(b.bill_number)}</TableCell>
                      <TableCell>{fmtDate(b.date)}</TableCell>
                      <TableCell className="text-right data-num">{inr(b.total)}</TableCell>
                      <TableCell className="text-right data-num text-success">{inr(b.paid)}</TableCell>
                      <TableCell className="text-right data-num text-destructive font-medium">{inr(b.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
